import {
  INIT_ACCOUNT,
  INIT_DEPARTMENT,
  KEEP_GLOBAL_ACCOUNT_ID_KEY,
  KEEP_GLOBAL_DEPARTMENT_KEY,
  KEEP_GLOBAL_FILTER_PENDING_SYNC_KEY,
} from "constants/filter";
import { findUniqueDepartments } from "utils/accountUtils";
import { findFromLocalStorage, removeFromLocalStorage, saveToLocalStorage } from "utils/localStorageUtils";

export const buildFilterConditions = (
  department: string | undefined,
  account: Account | undefined
): FilterConditions => {
  const global: GlobalFilterConditions = {};

  if (department) {
    global.department = department;
  }

  if (account?.id && account.id !== INIT_ACCOUNT.id) {
    global.contactId = account.id;
  }

  if (!global.department && !global.contactId) {
    return {};
  }

  return { global };
};

export const hasSavedGlobalFilter = (): boolean => {
  return (
    localStorage.getItem(KEEP_GLOBAL_DEPARTMENT_KEY) !== null ||
    localStorage.getItem(KEEP_GLOBAL_ACCOUNT_ID_KEY) !== null
  );
};

export const hasPendingGlobalFilterSync = (): boolean => {
  return localStorage.getItem(KEEP_GLOBAL_FILTER_PENDING_SYNC_KEY) === "true";
};

export const markPendingGlobalFilterSync = (): void => {
  saveToLocalStorage(KEEP_GLOBAL_FILTER_PENDING_SYNC_KEY, "true");
};

export const clearPendingGlobalFilterSync = (): void => {
  removeFromLocalStorage(KEEP_GLOBAL_FILTER_PENDING_SYNC_KEY);
};

export const readSavedDepartment = (): string | undefined => {
  if (localStorage.getItem(KEEP_GLOBAL_DEPARTMENT_KEY) === null) {
    return undefined;
  }

  const savedDepartment = findFromLocalStorage(KEEP_GLOBAL_DEPARTMENT_KEY, INIT_DEPARTMENT);
  return savedDepartment === INIT_DEPARTMENT ? undefined : savedDepartment;
};

export const resolveSavedAccount = (accountList: Account[], department: string | undefined): Account | undefined => {
  if (localStorage.getItem(KEEP_GLOBAL_ACCOUNT_ID_KEY) === null) {
    return undefined;
  }

  const savedAccountId = findFromLocalStorage(KEEP_GLOBAL_ACCOUNT_ID_KEY, INIT_ACCOUNT.id);
  if (savedAccountId === INIT_ACCOUNT.id) {
    return undefined;
  }

  const account = accountList.find((item) => item.id === savedAccountId);
  if (!account || account.id === INIT_ACCOUNT.id) {
    return undefined;
  }

  if (department && department !== INIT_DEPARTMENT && account.department !== department) {
    return undefined;
  }

  return account;
};

export const resolveAccountFromGlobalFilter = (
  accountList: Account[],
  global?: GlobalFilterConditions
): Account | undefined => {
  if (!global?.contactId) {
    return undefined;
  }

  const account = accountList.find((item) => item.id === global.contactId);
  if (!account) {
    return undefined;
  }

  if (global.department && account.department !== global.department) {
    return undefined;
  }

  return account;
};

export const isValidSavedDepartment = (accountList: Account[], department: string): boolean => {
  return findUniqueDepartments(accountList).some((item) => item.value === department);
};

export const clearDepartmentFilter = (): void => {
  removeFromLocalStorage(KEEP_GLOBAL_DEPARTMENT_KEY);
};

export const clearAccountFilter = (): void => {
  removeFromLocalStorage(KEEP_GLOBAL_ACCOUNT_ID_KEY);
};

export const clearGlobalFilterLocalStorage = (): void => {
  clearDepartmentFilter();
  clearAccountFilter();
  clearPendingGlobalFilterSync();
};

export const saveGlobalFilterToLocalStorage = (department: string | undefined, account: Account | undefined): void => {
  if (department) {
    saveToLocalStorage(KEEP_GLOBAL_DEPARTMENT_KEY, department);
  } else {
    clearDepartmentFilter();
  }

  if (account?.id && account.id !== INIT_ACCOUNT.id) {
    saveToLocalStorage(KEEP_GLOBAL_ACCOUNT_ID_KEY, account.id);
  } else {
    clearAccountFilter();
  }
};

export const hasServerGlobalFilter = (filterConditions?: FilterConditions): boolean => {
  const global = filterConditions?.global;
  return Boolean(global?.department || global?.contactId);
};

export const resolveGlobalFilterState = (
  accountList: Account[],
  filterConditions?: FilterConditions
): { department: string | undefined; account: Account | undefined } => {
  const global = filterConditions?.global;
  const department = global?.department;
  const account = resolveAccountFromGlobalFilter(accountList, global);

  return {
    department: department && isValidSavedDepartment(accountList, department) ? department : undefined,
    account,
  };
};

export const areFilterConditionsEqual = (left?: FilterConditions, right?: FilterConditions): boolean =>
  JSON.stringify(left ?? {}) === JSON.stringify(right ?? {});
